import { useRef, useState, useMemo } from "react";
import { Upload, FileText, CheckCircle2, UserPlus, AlertCircle } from "lucide-react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import Pill from "../ui/Pill";
import { Table, THead, TR, TH, TD } from "../ui/Table";
import { readCSVFile, stringifyCSV, downloadCSV } from "../../lib/csv";
import { parseGHLInvoices } from "../../lib/ghlImport";
import { findClientByEmail, clientKeyForEmail } from "../../lib/emailUtils";
import { formatUSD } from "../../lib/formatters";

const GHL_SAMPLE_HEADERS = [
  "Invoice name", "Invoice number", "Recurring Flag", "Line Item Name",
  "Line Item Description", "Line Item Amount", "Line Item Quantity",
  "Line Item Currency", "Customer Id", "Customer Name", "Customer Email",
  "Customer Phone No", "Issue Date", "Due Date", "Invoice Sub Total",
  "Invoice Total", "Status", "Live mode",
];

const GHL_SAMPLE_ROW = {
  "Invoice name": "Sample Invoice", "Invoice number": "INV-000001",
  "Recurring Flag": "No", "Line Item Name": "Outreach", "Line Item Description": "",
  "Line Item Amount": "$1,000.00", "Line Item Quantity": "1", "Line Item Currency": "USD",
  "Customer Id": "", "Customer Name": "Jane Doe", "Customer Email": "jane@business.com",
  "Customer Phone No": "", "Issue Date": "Apr 1, 2026", "Due Date": "Apr 1, 2026",
  "Invoice Sub Total": "1,000.00", "Invoice Total": "1,000.00", "Status": "Paid", "Live mode": "Yes",
};

const guessOfferAmount = (lineItemName) => {
  const n = (lineItemName || "").toLowerCase();
  if (n.includes("setup") || n.includes("pilot")) return 800;
  if (n.includes("trial")) return 300;
  if (n.includes("tech fee")) return 250;
  if (n.includes("done with you") || n.includes("dwy")) return 1500;
  return 1000;
};

export default function PaymentsCSVImport({ clients, offers, onCommit, onCreateClient, onCreateOffer }) {
  const fileRef = useRef(null);
  const [staged, setStaged] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const parsed = await readCSVFile(file);
      const rows = parseGHLInvoices(parsed, { clients, offers });
      setStaged({ rows, fileName: file.name });
    } catch (err) {
      alert("Could not parse CSV: " + err.message);
    }
    e.target.value = "";
  };

  const handleSample = () => {
    const csv = stringifyCSV([GHL_SAMPLE_ROW], GHL_SAMPLE_HEADERS);
    downloadCSV("payments-ghl-template.csv", csv);
  };

  const updateRow = (idx, patch) => {
    setStaged((s) => ({
      ...s,
      rows: s.rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const toggleSkip = (idx) => {
    updateRow(idx, { _skip: !staged.rows[idx]._skip });
  };

  const commit = () => {
    if (!staged) return;
    const rows = staged.rows.filter((r) => !r._skip);

    // Pass 1: create missing clients (grouped by domain/email key to avoid dupes)
    // Mutable maps that grow as we create new things during this commit
    const liveClients = [...clients];
    const liveOffers = [...offers];
    const offerByName = new Map(liveOffers.map((o) => [o.name.toLowerCase(), o]));

    // Group rows that lack a clientId by their client-key (domain or email)
    // so each unique customer is created exactly once
    const newClientByKey = new Map();
    rows.forEach((r) => {
      if (r.clientId) return;
      const key = clientKeyForEmail(r._ghlCustomerEmail);
      if (!key) return;
      if (newClientByKey.has(key)) return;
      if (!r._suggestClient) return;
      newClientByKey.set(key, r._suggestClient);
    });

    newClientByKey.forEach((data, key) => {
      const created = onCreateClient(data);
      if (created) {
        liveClients.push(created);
      }
    });

    // Pass 2: build payments. NO offer auto-creation — caller picks explicitly
    // in the preview, or uses "+ Create from CSV" to spawn one. Rows with no
    // offer still commit (offerId = null) so payments aren't lost.
    const toCommit = [];
    rows.forEach((r) => {
      let clientId = r.clientId;
      if (!clientId) {
        const matched = findClientByEmail(r._ghlCustomerEmail, liveClients);
        clientId = matched?.id;
      }
      if (!clientId) return; // nothing we can do — skip silently
      if (!(r.amount > 0)) return;

      toCommit.push({
        clientId,
        offerId: r.offerId || null,
        amount: r.amount,
        bookedDate: r.bookedDate,
        dueDate: r.dueDate,
        paidDate: r.paidDate,
        status: r.status,
        note: r.note ? `[Import] ${r.note}` : "[Import]",
      });
    });

    if (toCommit.length === 0) {
      alert("Nothing to import.");
      return;
    }
    onCommit(toCommit);
    setStaged(null);
  };

  const summary = useMemo(() => {
    if (!staged) return null;
    const rows = staged.rows;
    const willCreateKeys = new Set();
    rows.forEach((r) => {
      if (!r.clientId && r._suggestClient) {
        const k = clientKeyForEmail(r._ghlCustomerEmail);
        if (k) willCreateKeys.add(k);
      }
    });
    return {
      total: rows.length,
      matched: rows.filter((r) => r._matched && !r._skip).length,
      willCreate: willCreateKeys.size,
      skipped: rows.filter((r) => r._skip).length,
    };
  }, [staged]);

  return (
    <>
      <div className="inline-flex gap-1.5">
        <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
          <Upload size={13} /> Import GHL CSV
        </Button>
        <button onClick={handleSample} className="text-xs text-brand-slate hover:text-brand-blue inline-flex items-center gap-1 px-2 py-1.5">
          <FileText size={13} /> Sample
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
      </div>

      <Modal open={!!staged} onClose={() => setStaged(null)} title="Review GHL invoice import" size="xl">
        {staged && summary && (
          <div className="space-y-3">
            <div className="bg-brand-blue-soft border border-blue-200 rounded-lg p-3 text-xs text-brand-blue-dark space-y-1">
              <div className="font-medium">What this import will do:</div>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Match each invoice to a client by <strong>company domain</strong> (e.g. <span className="font-mono">accountant@hidmc.com</span> finds <span className="font-mono">prem@hidmc.com</span>). Generic emails (gmail, etc.) use exact match.</li>
                <li><strong>Auto-create</strong> any missing clients ({summary.willCreate} new).</li>
                <li>Pick an offer for each row, or click <strong>+ Create</strong> to make a new offer from the CSV line item.</li>
                <li>Rows without an offer still commit — they'll show as "No offer" until you set one.</li>
              </ul>
            </div>

            <div className="flex flex-wrap gap-2 text-xs">
              <Pill status="neutral">{summary.total} rows</Pill>
              <Pill status="active">{summary.matched} matched to existing</Pill>
              {summary.willCreate > 0 && <Pill status="scheduled">{summary.willCreate} new clients</Pill>}
              {summary.skipped > 0 && <Pill status="churned">{summary.skipped} skipped</Pill>}
            </div>

            <div className="max-h-[440px] overflow-auto border border-gray-100 dark:border-[var(--border-soft)] rounded-lg">
              <Table>
                <THead>
                  <TR>
                    <TH></TH>
                    <TH>From CSV</TH>
                    <TH>Client</TH>
                    <TH>Offer</TH>
                    <TH className="text-right">Amount</TH>
                    <TH>Booked</TH>
                    <TH>Status</TH>
                  </TR>
                </THead>
                <tbody>
                  {staged.rows.map((row, idx) => {
                    const isMatched = !!row.clientId;
                    return (
                      <TR key={idx} className={row._skip ? "opacity-40" : ""}>
                        <TD>
                          <input
                            type="checkbox"
                            checked={!row._skip}
                            onChange={() => toggleSkip(idx)}
                            className="cursor-pointer"
                          />
                        </TD>
                        <TD className="text-xs max-w-[180px]">
                          <div className="font-medium truncate">{row._ghlCustomerName || "—"}</div>
                          <div className="text-brand-slate truncate">{row._ghlCustomerEmail || "no email"}</div>
                          <div className="text-brand-slate text-[10px] truncate">{row._ghlLineItem}</div>
                        </TD>
                        <TD>
                          {isMatched ? (
                            <div className="flex items-center gap-1.5">
                              <CheckCircle2 size={12} className="text-emerald-600" />
                              <span className="text-xs">{clients.find((c) => c.id === row.clientId)?.name}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 text-blue-700">
                              <UserPlus size={12} />
                              <span className="text-xs">Will create: {row._suggestClient?.name || row._ghlCustomerEmail}</span>
                            </div>
                          )}
                        </TD>
                        <TD>
                          <div className="flex items-center gap-1">
                            <select
                              value={row.offerId || ""}
                              onChange={(e) => updateRow(idx, { offerId: e.target.value })}
                              className={`px-2 py-1 border rounded text-xs bg-white dark:bg-[var(--bg-soft)] min-w-[140px] ${row.offerId ? "border-gray-200" : "border-amber-300"}`}
                            >
                              <option value="">— pick offer —</option>
                              {offers.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                            {!row.offerId && row._ghlLineItem && onCreateOffer && (
                              <button
                                type="button"
                                onClick={() => {
                                  const created = onCreateOffer({
                                    name: row._ghlLineItem,
                                    type: row._ghlRecurring ? "recurring" : "oneoff",
                                    defaultAmount: guessOfferAmount(row._ghlLineItem),
                                    description: "Created from GHL import",
                                  });
                                  if (created) {
                                    // Update all rows with the same line item to use this new offer
                                    setStaged((s) => ({
                                      ...s,
                                      rows: s.rows.map((r) =>
                                        !r.offerId && r._ghlLineItem === row._ghlLineItem
                                          ? { ...r, offerId: created.id }
                                          : r
                                      ),
                                    }));
                                  }
                                }}
                                className="text-xs text-brand-blue hover:underline whitespace-nowrap"
                                title={`Create offer: ${row._ghlLineItem}`}
                              >
                                + Create
                              </button>
                            )}
                          </div>
                        </TD>
                        <TD className="text-right">
                          <input
                            type="number"
                            value={row.amount}
                            onChange={(e) => updateRow(idx, { amount: Number(e.target.value) })}
                            className="w-20 px-2 py-1 border border-gray-200 rounded text-xs text-right bg-white dark:bg-[var(--bg-soft)]"
                          />
                        </TD>
                        <TD>
                          <input
                            type="date"
                            value={row.bookedDate || ""}
                            onChange={(e) => updateRow(idx, { bookedDate: e.target.value })}
                            className="px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)]"
                          />
                        </TD>
                        <TD>
                          <select
                            value={row.status}
                            onChange={(e) => {
                              const status = e.target.value;
                              const paidDate = status === "paid" ? (row.paidDate || row.bookedDate) : null;
                              updateRow(idx, { status, paidDate });
                            }}
                            className="px-2 py-1 border border-gray-200 rounded text-xs bg-white dark:bg-[var(--bg-soft)]"
                          >
                            <option value="paid">Paid</option>
                            <option value="pending">Pending</option>
                            <option value="scheduled">Scheduled</option>
                          </select>
                        </TD>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className="flex justify-between items-center gap-2">
              <p className="text-xs text-brand-slate inline-flex items-center gap-1">
                <AlertCircle size={12} /> Clients and offers are auto-created in this same import.
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setStaged(null)}>Cancel</Button>
                <Button onClick={commit}>Import {summary.total - summary.skipped} rows</Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
